import { useEffect, useMemo, useState } from "react";

import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  SurveyConfiguration,
  SurveyGroupResponse,
  getOrgSurveyConfigPortal,
  getOrgSurveyGroups,
  getSurveyTranslations,
  patchOrgSurveyGroup,
  putSurveyGroupMemberships,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  isEnglishCode,
  languageCodeToTranslateLocale,
} from "@/common/constants/languages";

import GroupDetailsTab from "./GroupDetailsTab";
import GroupSurveysTab from "./GroupSurveysTab";
import styles from "./SurveyGroupMembersPage.module.scss";

const DETAILS_TAB = 0;
const SURVEYS_TAB = 1;

const SurveyGroupMembersPage: React.FC = () => {
  const { orgId, groupId } = useParams<{ orgId: string; groupId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const app = location.pathname.includes("rubiintel")
    ? "rubiintel"
    : "rubireportv2";
  const backPath = `/${app}/${orgId}/forms/groups`;

  const [group, setGroup] = useState<SurveyGroupResponse | null>(null);
  const [allConfigs, setAllConfigs] = useState<SurveyConfiguration[]>([]);
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingSurveys, setSavingSurveys] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(DETAILS_TAB);

  const [detailsName, setDetailsName] = useState("");
  const [detailsSortOrder, setDetailsSortOrder] = useState("");
  const [detailsHidden, setDetailsHidden] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);

  const [translatedNames, setTranslatedNames] = useState<
    Record<string, string>
  >({});
  const [selectedLang, setSelectedLang] = useState("");
  const [translatingLang, setTranslatingLang] = useState(false);
  const [translatingAll, setTranslatingAll] = useState(false);

  const nonEnglishLanguages = useMemo(
    () => supportedLanguages.filter((code) => !isEnglishCode(code)),
    [supportedLanguages],
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [groupsRes, configsRes] = await Promise.all([
          getOrgSurveyGroups({ path: { org_id: orgId ?? "" } }),
          getOrgSurveyConfigPortal({ path: { org_id: orgId ?? "" } }),
        ]);
        const loadedGroup =
          groupsRes.data?.groups.find(
            (candidate) => candidate.id === groupId,
          ) ?? null;
        if (!loadedGroup) {
          toast.error("Group not found");
          navigate(backPath);
          return;
        }

        setGroup(loadedGroup);
        setAllConfigs(configsRes.data?.surveys ?? []);
        setSupportedLanguages(configsRes.data?.supported_languages ?? []);
      } catch {
        toast.error("Failed to load group");
        navigate(backPath);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [app, groupId, navigate, orgId, backPath]);

  useEffect(() => {
    if (!group) return;
    const validConfigIds = new Set(allConfigs.map((config) => config.id));
    setSelectedIds(
      group.survey_config_ids.filter((id) => validConfigIds.has(id)),
    );
  }, [group, allConfigs]);

  useEffect(() => {
    if (!group) return;
    setDetailsName(group.name);
    setDetailsSortOrder(
      group.sort_order != null ? String(group.sort_order) : "",
    );
    setDetailsHidden(group.hidden);

    const existing =
      group.translated_names && typeof group.translated_names === "object"
        ? (group.translated_names as Record<string, string>)
        : {};
    setTranslatedNames(existing);
  }, [group]);

  useEffect(() => {
    if (!selectedLang && nonEnglishLanguages.length > 0) {
      setSelectedLang(nonEnglishLanguages[0]);
    }
  }, [nonEnglishLanguages, selectedLang]);

  const configNameById = useMemo(
    () => new Map(allConfigs.map((config) => [config.id, config.name])),
    [allConfigs],
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggleConfig = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((existingId) => existingId !== id);
      }
      return [...prev, id];
    });
  };

  const moveItem = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    setSelectedIds((prev) => {
      const sourceIndex = prev.indexOf(sourceId);
      const targetIndex = prev.indexOf(targetId);
      if (sourceIndex < 0 || targetIndex < 0) return prev;

      const next = [...prev];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  };

  const handleTranslateLang = async () => {
    if (!selectedLang || !detailsName.trim()) return;
    setTranslatingLang(true);
    try {
      const targetLocale = languageCodeToTranslateLocale(selectedLang);
      const res = await getSurveyTranslations({
        body: {
          source_strings: [{ text: detailsName.trim() }],
          target_language: targetLocale,
        },
      });
      const translated = res.data?.translations?.[0]?.translations?.[0]?.text;
      if (translated) {
        setTranslatedNames((prev) => ({ ...prev, [selectedLang]: translated }));
      } else {
        toast.warning("No translation returned");
      }
    } catch {
      toast.error("Translation failed");
    } finally {
      setTranslatingLang(false);
    }
  };

  const handleTranslateAll = async () => {
    if (!detailsName.trim() || nonEnglishLanguages.length === 0) return;
    setTranslatingAll(true);
    try {
      const langsToTranslate = nonEnglishLanguages.filter(
        (code) => !translatedNames[code]?.trim(),
      );
      if (langsToTranslate.length === 0) {
        toast.info("All languages already have translations");
        return;
      }
      for (const langCode of langsToTranslate) {
        const targetLocale = languageCodeToTranslateLocale(langCode);
        const res = await getSurveyTranslations({
          body: {
            source_strings: [{ text: detailsName.trim() }],
            target_language: targetLocale,
          },
        });
        const translated = res.data?.translations?.[0]?.translations?.[0]?.text;
        if (translated) {
          setTranslatedNames((prev) => ({ ...prev, [langCode]: translated }));
        }
      }
      toast.success("All languages translated");
    } catch {
      toast.error("Translation failed");
    } finally {
      setTranslatingAll(false);
    }
  };

  const translatedCount = nonEnglishLanguages.filter(
    (code) => !!translatedNames[code]?.trim(),
  ).length;

  const handleSaveDetails = async () => {
    if (!group) return;
    setSavingDetails(true);
    try {
      const res = await patchOrgSurveyGroup({
        path: { org_id: orgId ?? "", group_id: group.id },
        body: {
          name: detailsName.trim(),
          sort_order:
            detailsSortOrder !== "" ? parseInt(detailsSortOrder, 10) : null,
          hidden: detailsHidden,
          translated_names: translatedNames,
        },
      });
      setGroup(res.data!.group);
      toast.success("Group details saved");
    } catch {
      toast.error("Failed to save group details");
    } finally {
      setSavingDetails(false);
    }
  };

  const handleSaveSurveys = async () => {
    if (!group) return;
    setSavingSurveys(true);
    try {
      const memberships = selectedIds.map((id, index) => ({
        survey_config_id: id,
        sort_order: index,
      }));
      await putSurveyGroupMemberships({
        path: { org_id: orgId ?? "", group_id: group.id },
        body: { memberships },
      });
      toast.success("Group surveys updated");
    } catch {
      toast.error("Failed to update memberships");
    } finally {
      setSavingSurveys(false);
    }
  };

  const availableConfigs = allConfigs.filter(
    (config) => !selectedSet.has(config.id),
  );
  const normalizedSearch = search.trim().toLowerCase();
  const filteredAvailableConfigs =
    normalizedSearch.length === 0
      ? availableConfigs
      : availableConfigs.filter((config) =>
          config.name.toLowerCase().includes(normalizedSearch),
        );

  const navigateBack = () => navigate(backPath);

  return (
    <Box className={styles.root}>
      <Button
        onClick={navigateBack}
        startIcon={<ArrowBack />}
        className={styles.backButton}
      >
        Back to Survey Groups
      </Button>

      <Paper variant="outlined" className={styles.headerPaper}>
        <Box>
          <Typography variant="h5">Manage Group</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {group?.name ?? "Survey Group"}
          </Typography>
        </Box>
      </Paper>

      {loading ? (
        <Box className={styles.loadingBox}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box className={styles.tabsWrapper}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
            >
              <Tab label="Details" />
              <Tab label="Surveys" />
            </Tabs>
          </Box>

          {activeTab === DETAILS_TAB && (
            <GroupDetailsTab
              onNavigateBack={navigateBack}
              detailsName={detailsName}
              onDetailsNameChange={setDetailsName}
              detailsSortOrder={detailsSortOrder}
              onDetailsSortOrderChange={setDetailsSortOrder}
              detailsHidden={detailsHidden}
              onDetailsHiddenChange={setDetailsHidden}
              nonEnglishLanguages={nonEnglishLanguages}
              translatedCount={translatedCount}
              translatedNames={translatedNames}
              onTranslatedNamesChange={setTranslatedNames}
              selectedLang={selectedLang}
              onSelectedLangChange={setSelectedLang}
              translatingLang={translatingLang}
              translatingAll={translatingAll}
              onTranslateLang={handleTranslateLang}
              onTranslateAll={handleTranslateAll}
              onSaveDetails={handleSaveDetails}
              savingDetails={savingDetails}
            />
          )}

          {activeTab === SURVEYS_TAB && (
            <GroupSurveysTab
              onNavigateBack={navigateBack}
              selectedIds={selectedIds}
              configNameById={configNameById}
              onDragStart={setDraggingId}
              onDragEnd={() => setDraggingId(null)}
              onDragOver={(event) => {
                event.preventDefault();
              }}
              onDropOnItem={(targetId) => {
                if (draggingId) {
                  moveItem(draggingId, targetId);
                }
                setDraggingId(null);
              }}
              onRemoveSurvey={toggleConfig}
              availableCount={availableConfigs.length}
              search={search}
              onSearchChange={setSearch}
              filteredAvailableConfigs={filteredAvailableConfigs}
              selectedSet={selectedSet}
              onToggleConfig={toggleConfig}
              savingSurveys={savingSurveys}
              loading={loading}
              onSaveSurveys={handleSaveSurveys}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default SurveyGroupMembersPage;
